import { createFileRoute } from "@tanstack/react-router";
import { localProductionSection } from "@/lib/seo/local-production";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, serviceSchema, speakable, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad kostar eventfilm i Stockholm?",
    a: "En inspelningsdag med en fotograf och klipp ligger på 30 000–60 000 kr. Större konferens med multikamera, scenljud, intervjuer och flera versioner: 80 000–250 000 kr. Alla priser exklusive moms.",
  },
  {
    q: "Kan ni leverera samma dag?",
    a: "Ja. Vi klipper på plats och levererar en social-cut inom några timmar, ofta medan eventet fortfarande pågår. Huvudfilmen kommer inom 1–2 veckor.",
  },
  {
    q: "Vilka lokaler filmar ni i?",
    a: "Vi arbetar löpande i Stockholms konferens- och mässmiljöer — allt från hotellkonferens och kontorslokaler till större mässhallar. Vi gör alltid en teknisk recce av ljus och ljud innan.",
  },
  {
    q: "Hur snabbt kan ni boka in er?",
    a: "Med två veckors framförhållning löser vi de flesta event i Stockholm. Kortare varsel går ofta att lösa eftersom teamet är baserat här.",
  },
  {
    q: "Får vi ljudet från scenen?",
    a: "Ja. Vi kopplar oss mot husets ljudbord och kompletterar med egna mikrofoner, så talarnas ljud blir användbart även i publikmiljö.",
  },
];

export const Route = createFileRoute("/eventfilm_/stockholm")({
  head: () => ({
    meta: [
      { title: "Eventfilmare i Stockholm — reels innan gästerna gått hem" },
      {
        name: "description",
        content:
          "Eventfilm i Stockholm för konferenser, mässor och lanseringar. Reels publicerade medan eventet pågår, huvudfilm på 1–2 veckor. Pris 30 000–250 000 kr — se exempel.",
      },
      { property: "og:title", content: "Eventfilmare i Stockholm — reels innan gästerna gått hem" },
      {
        property: "og:description",
        content:
          "Eventproduktion i Stockholm med same-day leverans, multikamera och talarintervjuer.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/eventfilm/stockholm" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/eventfilm/stockholm" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/eventfilm/stockholm", name: "Eventfilm Stockholm — reels samma dag, film på 1–2 veckor", description: "Eventfilm i Stockholm för konferenser, mässor och lanseringar. Reels publicerade medan eventet pågår, huvudfilm på 1–2 veckor. Pris 30 000–250 000 kr — se exempel."})) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Eventfilm Stockholm",
            path: "/eventfilm/stockholm",
            serviceType: "Eventfilm-produktion",
            description:
              "Eventfilm i Stockholm för konferenser, mässor, lanseringar och kundträffar — same-day social-cut och huvudfilm.",
            priceRange: "30 000–250 000 kr",
            areaServed: ["Stockholm"],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Eventfilm", path: "/eventfilm" },
            { name: "Stockholm", path: "/eventfilm/stockholm" },
          ]),
        ),
      },
    ],
  }),
  component: EventfilmStockholmPage,
});

function EventfilmStockholmPage() {
  return (
    <LandingPage
      eyebrow="Eventfilm Stockholm"
      title={<>Eventfilm i Stockholm — publicerad innan gästerna gått hem.</>}
      lede="Fenny filmar konferenser, mässor, lanseringar och kundträffar i Stockholm. Vi klipper på plats och levererar social-cut samma dag, huvudfilm och talarintervjuer inom 1–2 veckor. Teamet är baserat i Stockholm, så restid och riggtid blir kort."
      tldr={
        <p>
          Eventfilm i Stockholm kostar <strong>30 000–60 000 kr</strong> för en
          dag med en fotograf och klipp, och{" "}
          <strong>80 000–250 000 kr</strong> för multikameraproduktion med
          scenljud, intervjuer och versionering. Same-day social-cut ingår i
          båda upplägg.
        </p>
      }
      ctaPrimary={{ label: "Boka för ert event", to: "/kontakt" }}
      ctaSecondary={{ label: "Se case", to: "/case" }}
      trackingId="eventfilm-stockholm"
      bullets={[
        { title: "Same-day social", body: "Reels och stories klara medan eventet pågår." },
        { title: "På plats hos er", body: "Baserade i Stockholm — kort inställelsetid och kända lokaler." },
        { title: "Scenljud direkt", body: "Vi kopplar mot husets ljudbord så talarna hörs." },
      ]}
      pricing={{
        heading: "Vad kostar eventfilm i Stockholm?",
        intro: "Riktpriser exklusive moms. Exakt pris sätts efter agenda och lokal.",
        rows: [
          { format: "Kundträff / mindre event", scope: "1 fotograf, 1 dag, social-cut + highlight", price: "30 000–60 000 kr", lead: "same-day + 1 vecka" },
          { format: "Konferens med scen", scope: "2 kameror, scenljud, talarintervjuer", price: "80 000–150 000 kr", lead: "same-day + 2 veckor" },
          { format: "Mässa / lansering", scope: "3 kameror, producent, B-roll-bibliotek, flera versioner", price: "150 000–250 000 kr", lead: "same-day + 2 veckor" },
        ],
        footnote: "Alla priser exklusive moms. Flera event under året samlas med fördel i ett Pulse-abonnemang.",
      }}
      sections={[
        localProductionSection("stockholm", "Stockholm"),
        {
          id: "upplagg",
          heading: "Så lägger vi upp en eventdag i Stockholm",
          body: (
            <p>
              Recce av lokalen dagen innan eller på morgonen: ljus, ljud,
              kameraplaceringar och var intervjuerna kan spelas in. Under dagen
              filmar vi scen, mingel och detaljer parallellt med att en
              redigerare klipper social-materialet på plats. Pauserna används
              till talar- och kundintervjuer som senare blir egna{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/kundcase-film">
                kundcase
              </a>
              .
            </p>
          ),
        },
        {
          id: "efterat",
          heading: "Vad ni gör med materialet efteråt",
          body: (
            <p>
              Huvudfilmen driver anmälningar till nästa edition. Talarklippen
              blir innehåll i 4–8 veckor efter eventet. B-roll-biblioteket
              används i kampanjer och rekrytering. Behöver ni löpande flöde
              under hela året är{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/priser">
                abonnemang
              </a>{" "}
              nästan alltid billigare än enstaka eventinsatser.
            </p>
          ),
        },
        {
          id: "ovriga-orter",
          heading: "Andra orter",
          body: (
            <p>
              Vi filmar event i hela Sverige — se{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/eventfilm/goteborg">
                eventfilm i Göteborg
              </a>{" "}
              eller{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/eventfilm">
                eventfilm generellt
              </a>
              .
            </p>
          ),
        },
      ]}
      faq={FAQ}
      caseLimit={3}
      riskReducers
      closing={{
        title: "Event i Stockholm på gång?",
        body: "Boka 30 minuter så planerar vi täckning, leverans och pris.",
        primary: { label: "Boka möte", to: "/kontakt" },
        secondary: { label: "Se priser", to: "/priser" },
      }}
    />
  );
}
